import { useCallback, useEffect, useRef, useReducer, Reducer } from 'react'
import { emptyObj } from 'utils/fp'
import { isEmpty } from 'ramda'
import DeleteAction from 'core/actions/DeleteAction'
import CustomAction from 'core/actions/CustomAction'
import { IDataKeys } from 'k8s/datakeys.model'
import { ArrayElement } from 'core/actions/Action'
import { generateApiErrorPayload } from 'api-client/helpers'
import { ApiResponseErrorModel } from 'api-client/model'

interface DeleteState<R> {
  deleting: boolean
  success: boolean
  error: ReturnType<typeof generateApiErrorPayload>
  result: R | void
}

interface DeleteReducerAction<R> {
  type: 'startDelete' | 'endDelete' | 'reset'
  payload?: {
    result?: R | void
    error?: ApiResponseErrorModel
    success?: boolean
  }
}

type DeleteActionReducer<R> = Reducer<DeleteState<R>, DeleteReducerAction<R>>

const initialState = {
  deleting: false,
  success: false,
  error: null,
  result: null,
}

function deleteActionReducer<R>(
  state: DeleteState<R>,
  { type, payload }: DeleteReducerAction<R>,
): DeleteState<R> {
  switch (type) {
    case 'reset':
      return initialState
    case 'startDelete':
      return {
        deleting: true,
        success: null,
        error: null,
        result: null,
      }
    case 'endDelete':
    default:
      return {
        deleting: false,
        result: payload?.result || null,
        error: generateApiErrorPayload(payload?.error || null),
        success: !!payload?.success,
      }
  }
}

const useDeleteAction = <
  D extends keyof IDataKeys,
  P extends Record<string, unknown> = Record<string, unknown>,
  R = ArrayElement<IDataKeys[D]>
>(
  action: DeleteAction<D, P, R> | CustomAction<D, P, R>,
) => {
  const unmounted = useRef(false)
  const deletePromisesBuffer = useRef([])

  const [{ deleting, success, result, error }, localDispatch] = useReducer<DeleteActionReducer<R>>(
    deleteActionReducer,
    initialState,
  )

  const reset = useCallback(() => localDispatch({ type: 'reset' }), [])

  const remove = useCallback(
    async (params = emptyObj) => {
      let response = null
      let success = true
      let error = null

      if (isEmpty(deletePromisesBuffer.current)) {
        localDispatch({ type: 'startDelete' })
      }

      try {
        const currentPromise = (async () => {
          await Promise.all(deletePromisesBuffer.current)
          const result = await action.call(params as P, { propagateError: true })
          deletePromisesBuffer.current.shift()
          return result
        })()

        deletePromisesBuffer.current.push(currentPromise)
        response = await currentPromise

        if (isEmpty(deletePromisesBuffer.current) && !unmounted.current) {
          localDispatch({ type: 'endDelete', payload: { result: response, success: true } })
        }
      } catch (err) {
        deletePromisesBuffer.current.shift()
        localDispatch({ type: 'endDelete', payload: { success: false, error: err } })
        success = false
        error = err?.err
      }

      return { success, response, error }
    },
    [action],
  )

  const bulkRemove = useCallback(
    async (items: P[]) => {
      localDispatch({ type: 'startDelete' })

      const results = await Promise.all(
        items?.map(async (params) => {
          try {
            const result = await action.call(params, { propagateError: true })
            return { success: true, response: result, error: null, params }
          } catch (err) {
            return {
              success: false,
              response: null,
              error: generateApiErrorPayload(err),
              params,
            }
          }
        }),
      )

      const allSuccessful = results?.every((r) => r.success)
      const firstError = results?.find((r) => !r.success)?.error

      if (!unmounted.current) {
        localDispatch({
          type: 'endDelete',
          payload: {
            result: (results?.map((r) => r?.response) as unknown) as R,
            success: allSuccessful,
            error: firstError,
          },
        })
      }

      return results
    },
    [action],
  )

  useEffect(() => {
    return () => {
      unmounted.current = true
    }
  }, [])

  return { remove, bulkRemove, deleting, success, result, error, reset }
}

export default useDeleteAction
